import { format, isAfter, subDays, subMonths, subYears } from 'date-fns';
import { useSnapshot } from 'valtio';

import { FromStore, Project, ProjectType } from '@cartwhl/common/types';
import Text from '@cartwhl/ui/base/Text';
import Button from '@cartwhl/ui/input/Button';

import { projectStore, userStore } from 'stores';
import { LEGACY_PROJECT_ID } from 'stores/projectStore';

import { ProjectCard } from './ProjectCard';
import { ProjectList } from './ProjectList';

// Cutoff date: November 16, 2025 (end of day)
const LEGACY_PROJECT_CUTOFF_DATE = new Date('2025-11-18T23:59:59.999Z');

export function ProjectSection({
  searchQuery = '',
  viewMode = 'grid',
  filterType = 'all',
  sortType = 'date-created',
}: {
  searchQuery?: string;
  viewMode?: 'grid' | 'list';
  filterType?: 'all' | 'motion' | 'pose';
  sortType?: 'date-created' | 'last-edited';
}) {
  const { allProjects, bookmarkedProjects } = useSnapshot(projectStore.state);
  const { userInfo } = useSnapshot(userStore.state);

  // Check if user account was created on or before the cutoff date
  const userCreatedAt = userInfo?.createdAt
    ? new Date(userInfo.createdAt)
    : null;
  const userQualifiesForLegacyProject =
    userCreatedAt && userCreatedAt <= LEGACY_PROJECT_CUTOFF_DATE;

  const legacyProjectName = 'Legacy project';
  const searchMatchesLegacy =
    !searchQuery ||
    legacyProjectName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    searchQuery.toLowerCase().includes('legacy') ||
    searchQuery.toLowerCase().includes('project');
  const shouldShowLegacyProject =
    userQualifiesForLegacyProject && searchMatchesLegacy;

  if (!allProjects.data) return null;

  // Get all bookmarked projects and show data even during 'pending' status to prevent flash when reloading
  const bookmarkedProjectsList = bookmarkedProjects.data
    ? bookmarkedProjects.data
    : [];

  // Filter projects by search query and filter type
  let filteredProjects = allProjects.data.items;
  if (searchQuery) {
    filteredProjects = filteredProjects.filter((project) =>
      project.projectName.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }
  if (filterType !== 'all') {
    filteredProjects = filteredProjects.filter(
      (project) =>
        (filterType === 'motion' && project.type === ProjectType.MOTION) ||
        (filterType === 'pose' && project.type === ProjectType.POSE),
    );
  }

  // Filter bookmarked projects
  let filteredBookmarkedProjects = bookmarkedProjectsList;
  if (searchQuery) {
    filteredBookmarkedProjects = filteredBookmarkedProjects.filter((project) =>
      project.projectName.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }
  if (filterType !== 'all') {
    filteredBookmarkedProjects = filteredBookmarkedProjects.filter(
      (project) =>
        (filterType === 'motion' && project.type === ProjectType.MOTION) ||
        (filterType === 'pose' && project.type === ProjectType.POSE),
    );
  }

  const shouldShowBookmarks =
    !searchQuery || filteredBookmarkedProjects.length > 0;

  const groupedProjects = groupProjectsByCreationDate(
    filteredProjects,
    filteredBookmarkedProjects,
    shouldShowLegacyProject ?? false,
    shouldShowBookmarks,
    sortType,
  );

  // Define section order - use renamed section names when sorting by last-edited
  const sectionOrder = [
    ...(shouldShowBookmarks ? ['Bookmarks'] : []),
    sortType === 'last-edited' ? 'Edited this week' : 'Created this week',
    sortType === 'last-edited' ? 'Edited this month' : 'Created this month',
    'Other projects',
    ...(shouldShowLegacyProject ? ['Legacy project'] : []),
  ];

  // Helper function to extract preview URL for project card
  const getProjectPreview = (project: FromStore<Project>): string => {
    if (project.projectThumbnailURL) {
      return project.projectThumbnailURL;
    }

    // Otherwise show empty state
    return '';
  };

  // Render sections in order
  const orderedSections = sectionOrder
    .filter((key) => groupedProjects[key] !== undefined)
    .map((key) => [key, groupedProjects[key]] as const);

  const remainingSections = Object.entries(groupedProjects).filter(
    ([key]) => !sectionOrder.includes(key),
  );

  const allProjectsInOrder: Array<FromStore<Project> & { sectionKey: string }> =
    [];
  [...orderedSections, ...remainingSections].forEach(
    ([sectionKey, projects]) => {
      allProjectsInOrder.push(...projects.map((p) => ({ ...p, sectionKey })));
    },
  );

  return (
    <>
      {[...orderedSections, ...remainingSections].map(([key, value], i) => (
        <ProjectList
          key={`${key}-${i}`}
          title={key}
          amount={value.length}
          viewMode={viewMode}
        >
          {value.length === 0 && (
            <div className="px-4">
              <Text variant="studio/body/md">No projects found</Text>
            </div>
          )}
          {value.map((project, j) => (
            <ProjectCard
              key={`${project.projectID}-${j}`}
              projectID={project.projectID}
              title={project.projectName}
              type={project.type === ProjectType.MOTION ? 'animate' : 'pose'}
              preview={getProjectPreview(project)}
              lastUpdatedAt={project.lastUpdatedAt ?? project.createdAt}
              createdAt={project.createdAt}
              isBookmarked={project.isBookmarked}
              allProjectsInOrder={allProjectsInOrder}
              sectionKey={key}
              viewMode={viewMode}
            />
          ))}
        </ProjectList>
      ))}
    </>
  );
}

export const ProjectSectionLoading = () => {
  return (
    <div className="flex flex-col gap-5 py-8">
      <div className="bg-gray-30 my-[5px] ml-3 h-5 w-40 animate-pulse rounded-lg" />
      <div className="flex gap-4">
        <div className="flex flex-1 flex-col gap-3">
          <div className="bg-gray-30 aspect-video animate-pulse rounded-2xl" />
          <div className="bg-gray-30 h-5 w-40 animate-pulse rounded-lg" />
        </div>
        <div className="flex flex-1 flex-col gap-4">
          <div className="bg-gray-30 aspect-video animate-pulse rounded-2xl" />
          <div className="bg-gray-30 h-5 w-40 animate-pulse rounded-lg" />
        </div>
        <div className="flex-1" />
        <div className="flex-1" />
      </div>
    </div>
  );
};

export const ProjectSectionError = ({
  error,
  sortType,
}: {
  error: string;
  sortType?: 'date-created' | 'last-edited';
}) => {
  return (
    <div className="flex flex-col gap-5 py-8">
      <div className="flex items-center justify-between px-4">
        <Text as="h2" variant="studio/body/md">
          Error: {error}
        </Text>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => projectStore.loadProjects(sortType || 'date-created')}
        >
          Retry
        </Button>
      </div>
    </div>
  );
};

const getProjectGroups = (sortType: 'date-created' | 'last-edited') => {
  const dateField = sortType === 'last-edited' ? 'lastUpdatedAt' : 'createdAt';
  const weekKey =
    sortType === 'last-edited' ? 'Edited this week' : 'Created this week';
  const monthKey =
    sortType === 'last-edited' ? 'Edited this month' : 'Created this month';

  return {
    ['Bookmarks']: (project: FromStore<Project>) => !!project.isBookmarked,
    ['Legacy project']: (project: FromStore<Project>) =>
      project.projectID === LEGACY_PROJECT_ID,
    [weekKey]: (project: FromStore<Project>) => {
      const date =
        dateField === 'lastUpdatedAt'
          ? (project.lastUpdatedAt ?? project.createdAt)
          : project.createdAt;
      return isAfter(new Date(date), subDays(new Date(), 7));
    },
    [monthKey]: (project: FromStore<Project>) => {
      const date =
        dateField === 'lastUpdatedAt'
          ? (project.lastUpdatedAt ?? project.createdAt)
          : project.createdAt;
      return isAfter(new Date(date), subMonths(new Date(), 1));
    },
    ['Other projects']: () => true,
  };
};

function groupProjectsByCreationDate(
  projects: FromStore<Project[]>,
  bookmarkedProjects: FromStore<Project[]>,
  shouldShowLegacyProject: boolean,
  shouldShowBookmarks: boolean,
  sortType: 'date-created' | 'last-edited' = 'date-created',
) {
  const output: Record<string, FromStore<Project>[]> = {
    ...(shouldShowBookmarks ? { Bookmarks: [] } : {}),
    ...(shouldShowLegacyProject ? { 'Legacy project': [] } : {}),
  };

  const ProjectGroups = getProjectGroups(sortType);
  const dateField = sortType === 'last-edited' ? 'lastUpdatedAt' : 'createdAt';

  if (shouldShowBookmarks) {
    output['Bookmarks'] = [...bookmarkedProjects];
  }

  // Extract legacy project first (only if user qualifies)
  if (shouldShowLegacyProject) {
    const legacyProject = projects.find(
      (project) => project.projectID === LEGACY_PROJECT_ID,
    );
    if (legacyProject) {
      output['Legacy project'] = [legacyProject];
    }
  }

  for (const project of projects) {
    // Skip legacy project (already handled above, or if user doesn't qualify)
    if (project.projectID === LEGACY_PROJECT_ID) {
      continue;
    }

    for (const [key, test] of Object.entries(ProjectGroups)) {
      // Skip Bookmarks and Legacy project in this loop (already handled above)
      if (key === 'Bookmarks' || key === 'Legacy project') continue;

      if (key === 'rest') {
        const projectDate =
          dateField === 'lastUpdatedAt'
            ? (project.lastUpdatedAt ?? project.createdAt)
            : project.createdAt;
        const date = new Date(projectDate);
        if (isAfter(date, subYears(new Date(), 1))) {
          output[format(date, 'MMMM')] = output[format(date, 'MMMM')] || [];
          output[format(date, 'MMMM')].push(project);
        } else {
          output[format(date, 'MMMM YYY')] =
            output[format(date, 'MMMM YYY')] || [];
          output[format(date, 'MMMM YYY')].push(project);
        }
      } else if (test(project)) {
        output[key] = output[key] || [];
        output[key].push(project);
        break;
      }
    }
  }

  // Sort projects within each section by the appropriate date field
  for (const key in output) {
    if (key === 'Legacy project') continue;
    output[key].sort((a, b) => {
      const dateA =
        sortType === 'last-edited'
          ? (a.lastUpdatedAt ?? a.createdAt)
          : a.createdAt;
      const dateB =
        sortType === 'last-edited'
          ? (b.lastUpdatedAt ?? b.createdAt)
          : b.createdAt;
      return new Date(dateB).getTime() - new Date(dateA).getTime(); // Most recent first
    });
  }

  // Update section titles based on sort type
  if (sortType === 'last-edited') {
    const renamed: Record<string, FromStore<Project>[]> = {};
    for (const key in output) {
      if (key === 'Created this week') {
        renamed['Edited this week'] = output[key];
      } else if (key === 'Created this month') {
        renamed['Edited this month'] = output[key];
      } else {
        renamed[key] = output[key];
      }
    }
    return renamed;
  }

  return output;
}
